// src/pages/AdminProductsPage.js
import React, { useState, useEffect } from 'react';
import AdminProductForm from '../components/AdminProductForm';
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getCategories, createCategory, updateCategory, deleteCategory,
} from '../api/apiService';

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const inputStyle = {
  ...bs, padding: '10px 14px', border: '1px solid #e5e0e0',
  fontSize: '13px', color: '#060D14', outline: 'none',
  borderRadius: '2px', transition: 'border-color 0.2s', background: 'white',
};

// ── Tab button ────────────────────────────────────────────────────────────
const TabBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{
    ...bs, background: 'none', border: 'none', cursor: 'pointer',
    padding: '10px 20px', fontSize: '11px', fontWeight: 600,
    letterSpacing: '0.15em', textTransform: 'uppercase',
    color: active ? '#961914' : '#aaa',
    borderBottom: active ? '2px solid #961914' : '2px solid transparent',
    marginBottom: '-1px', transition: 'all 0.2s',
  }}
  onMouseOver={e => { if (!active) e.currentTarget.style.color = '#060D14'; }}
  onMouseOut={e => { if (!active) e.currentTarget.style.color = '#aaa'; }}>
    {children}
  </button>
);

// ══════════════════════════════════════════════════════════════════════════
// PRODUCTOS
// ══════════════════════════════════════════════════════════════════════════
const ProductsTab = () => {
  const [products,       setProducts]       = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm,       setShowForm]       = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [deleting,       setDeleting]       = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setProducts(await getProducts()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleFormSubmit = async (formData) => {
    try {
      editingProduct
        ? await updateProduct(editingProduct.id, formData)
        : await createProduct(formData);
      setShowForm(false);
      await load();
    } catch (e) { alert('No se pudo guardar el producto.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto? Esta acción no se puede deshacer.')) return;
    setDeleting(id);
    try { await deleteProduct(id); await load(); }
    catch (e) { alert('No se pudo eliminar el producto.'); }
    finally { setDeleting(null); }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.categoryName || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalStock = products.reduce((s, p) =>
    s + p.variants.reduce((vs, v) =>
      vs + Object.values(v.storeStocks || {}).reduce((a, b) => a + b, 0), 0), 0);

  // Vista formulario
  if (showForm) return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: '24px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={() => setShowForm(false)} style={{
          ...bs, background: 'none', border: '1px solid #e5e0e0', cursor: 'pointer',
          padding: '8px 16px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: '#666', borderRadius: '2px',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
          Volver
        </button>
        <h2 style={{ ...ds, fontSize: '1.8rem', fontWeight: 400, color: '#060D14', margin: 0 }}>
          {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        </h2>
      </div>
      <AdminProductForm
        product={editingProduct}
        onSubmit={handleFormSubmit}
        onCancel={() => setShowForm(false)}
      />
    </div>
  );

  // Vista lista
  return (
    <div>
      {/* Sub-header */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: '20px',
        flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ ...bs, fontSize: '12px', color: '#aaa', margin: 0 }}>
          {products.length} productos · {totalStock} unidades en stock
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Buscador */}
          <div style={{ position: 'relative' }}>
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#bbb"
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar…"
              style={{ ...inputStyle, paddingLeft: '30px', width: '200px' }}
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
          <button onClick={() => { setEditingProduct(null); setShowForm(true); }} style={{
            ...bs, background: '#961914', color: 'white', border: 'none',
            padding: '10px 20px', cursor: 'pointer', fontSize: '11px',
            fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase',
            borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '7px',
            transition: 'background 0.2s',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#7a140f'}
          onMouseOut={e => e.currentTarget.style.background = '#961914'}>
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Nuevo Producto
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ display: 'grid',
          gridTemplateColumns: '48px 1fr 100px 120px 80px 100px',
          padding: '10px 16px', background: '#faf9f9',
          borderBottom: '1px solid #f0eaea' }}>
          {['', 'Producto', 'Precio', 'Categoría', 'Stock', 'Acciones'].map(h => (
            <span key={h} style={{ ...bs, fontSize: '10px', fontWeight: 600,
              letterSpacing: '0.18em', textTransform: 'uppercase', color: '#bbb' }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>Cargando productos…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>
              {search ? 'Sin resultados.' : 'No hay productos registrados.'}
            </p>
          </div>
        ) : filtered.map((p, idx) => {
          const stock = p.variants.reduce((s, v) =>
            s + Object.values(v.storeStocks || {}).reduce((a, b) => a + b, 0), 0);
          const isLow = stock <= (p.lowStockThreshold || 5);

          return (
            <div key={p.id} style={{
              display: 'grid', gridTemplateColumns: '48px 1fr 100px 120px 80px 100px',
              padding: '12px 16px', alignItems: 'center',
              borderBottom: idx < filtered.length - 1 ? '1px solid #f8f5f5' : 'none',
              background: idx % 2 === 0 ? 'white' : '#fdfafa', transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#fdf5f5'}
            onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fdfafa'}>

              {/* Imagen */}
              <div style={{ width: '36px', height: '36px', borderRadius: '2px',
                overflow: 'hidden', background: '#f5f2f2' }}>
                {p.images?.[0]
                  ? <img src={p.images[0]} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#ccc">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"/>
                      </svg>
                    </div>
                }
              </div>

              {/* Nombre */}
              <div style={{ paddingRight: '12px' }}>
                <p style={{ ...ds, fontSize: '1rem', fontWeight: 600, color: '#060D14',
                  margin: '0 0 1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {p.name}
                </p>
                <p style={{ ...bs, fontSize: '10px', color: '#ccc', margin: 0 }}>
                  {p.variants.length} variante{p.variants.length !== 1 ? 's' : ''}
                </p>
              </div>

              <span style={{ ...ds, fontSize: '1.05rem', color: '#961914' }}>
                ${Number(p.price).toFixed(2)}
              </span>

              {p.categoryName
                ? <span style={{ ...bs, fontSize: '10px', fontWeight: 600, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: '#961914',
                    background: 'rgba(150,25,20,0.06)', padding: '3px 9px',
                    borderRadius: '20px', display: 'inline-block' }}>
                    {p.categoryName}
                  </span>
                : <span style={{ ...bs, fontSize: '11px', color: '#ddd' }}>—</span>
              }

              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ ...bs, fontSize: '13px', fontWeight: 600,
                  color: isLow ? '#dc2626' : '#16a34a' }}>{stock}</span>
                {isLow && (
                  <svg width="11" height="11" fill="#dc2626" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                )}
              </div>

              <div style={{ display: 'flex', gap: '5px' }}>
                <button onClick={() => { setEditingProduct(p); setShowForm(true); }} style={{
                  ...bs, background: 'none', border: '1px solid #e5e0e0', cursor: 'pointer',
                  padding: '5px 10px', fontSize: '10px', fontWeight: 600,
                  letterSpacing: '0.1em', textTransform: 'uppercase', color: '#666',
                  borderRadius: '2px', transition: 'all 0.2s',
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
                  Editar
                </button>
                <button onClick={() => handleDelete(p.id)} disabled={deleting === p.id}
                  style={{ ...bs, background: 'none', border: '1px solid #fee2e2',
                    cursor: 'pointer', padding: '5px 7px', color: '#dc2626',
                    borderRadius: '2px', transition: 'all 0.2s', opacity: deleting === p.id ? 0.5 : 1 }}
                  onMouseOver={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}>
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// CATEGORÍAS
// ══════════════════════════════════════════════════════════════════════════
const CategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [newName,    setNewName]    = useState('');
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { setCategories(await getCategories()); }
    catch (e) { console.error(e); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await createCategory({ name: newName.trim() });
      setNewName('');
      await load();
    } catch (e) { alert('Error al crear categoría.'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editing?.name.trim()) return;
    setSaving(true);
    try {
      await updateCategory({ id: editing.id, name: editing.name.trim() });
      setEditing(null);
      await load();
    } catch (e) { alert('Error al actualizar categoría.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    try { await deleteCategory(id); await load(); }
    catch (e) { alert('Error al eliminar categoría.'); }
  };

  return (
    <div style={{ maxWidth: '640px' }}>

      {/* Formulario */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        padding: '20px 24px', borderRadius: '2px', marginBottom: '16px' }}>
        <p style={{ ...bs, fontSize: '10px', fontWeight: 600, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: '#bbb', marginBottom: '12px' }}>
          {editing ? 'Editar categoría' : 'Nueva categoría'}
        </p>
        <form onSubmit={editing ? handleUpdate : handleAdd}
          style={{ display: 'flex', gap: '10px' }}>
          <input type="text" required
            value={editing ? editing.name : newName}
            onChange={e => editing
              ? setEditing({ ...editing, name: e.target.value })
              : setNewName(e.target.value)}
            placeholder="Nombre de la categoría"
            style={{ ...inputStyle, flex: 1 }}
            onFocus={e => e.target.style.borderColor = '#961914'}
            onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          <button type="submit" disabled={saving} style={{
            ...bs, background: '#961914', color: 'white', border: 'none',
            padding: '10px 20px', cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em',
            textTransform: 'uppercase', borderRadius: '2px',
            opacity: saving ? 0.7 : 1, transition: 'background 0.2s', whiteSpace: 'nowrap',
          }}
          onMouseOver={e => { if (!saving) e.currentTarget.style.background = '#7a140f'; }}
          onMouseOut={e => e.currentTarget.style.background = '#961914'}>
            {saving ? 'Guardando…' : editing ? 'Actualizar' : 'Agregar'}
          </button>
          {editing && (
            <button type="button" onClick={() => setEditing(null)} style={{
              ...bs, background: 'none', border: '1px solid #e5e0e0', color: '#666',
              padding: '10px 16px', cursor: 'pointer', fontSize: '11px',
              fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
              borderRadius: '2px', transition: 'all 0.2s', whiteSpace: 'nowrap',
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
              Cancelar
            </button>
          )}
        </form>
      </div>

      {/* Lista */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ padding: '10px 20px', background: '#faf9f9',
          borderBottom: '1px solid #f0eaea' }}>
          <span style={{ ...bs, fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: '#bbb' }}>
            {categories.length} categoría{categories.length !== 1 ? 's' : ''}
          </span>
        </div>

        {categories.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <p style={{ ...bs, fontSize: '12px', color: '#bbb' }}>
              No hay categorías registradas.
            </p>
          </div>
        ) : (
          categories.map((cat, idx) => (
            <div key={cat.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px',
              borderBottom: idx < categories.length - 1 ? '1px solid #faf5f5' : 'none',
              background: idx % 2 === 0 ? 'white' : '#fdfafa', transition: 'background 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.background = '#fdf5f5'}
            onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fdfafa'}>
              <span style={{ ...ds, fontSize: '1.05rem', fontWeight: 600, color: '#060D14' }}>
                {cat.name}
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => setEditing({ ...cat })} style={{
                  ...bs, background: 'none', border: '1px solid #e5e0e0',
                  cursor: 'pointer', padding: '5px 12px', fontSize: '10px',
                  fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: '#666', borderRadius: '2px', transition: 'all 0.2s',
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#666'; }}>
                  Editar
                </button>
                <button onClick={() => handleDelete(cat.id)} style={{
                  ...bs, background: 'none', border: '1px solid #fee2e2',
                  cursor: 'pointer', padding: '5px 8px', color: '#dc2626',
                  borderRadius: '2px', transition: 'all 0.2s',
                }}
                onMouseOver={e => e.currentTarget.style.background = '#fef2f2'}
                onMouseOut={e => e.currentTarget.style.background = 'none'}>
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL — fusión
// ══════════════════════════════════════════════════════════════════════════
const AdminProductsPage = () => {
  const [tab, setTab] = useState('products');

  return (
    <div style={{ padding: '24px 16px' }}>

      {/* Header */}
      <div style={{ marginBottom: '4px' }}>
        <h1 style={{ ...ds, fontSize: '2rem', fontWeight: 400, color: '#060D14', margin: '0 0 16px' }}>
          {tab === 'products' ? 'Productos' : 'Categorías'}
        </h1>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #f0eaea', marginBottom: '24px' }}>
        <TabBtn active={tab === 'products'} onClick={() => setTab('products')}>
          Productos
        </TabBtn>
        <TabBtn active={tab === 'categories'} onClick={() => setTab('categories')}>
          Categorías
        </TabBtn>
      </div>

      {tab === 'products'   && <ProductsTab />}
      {tab === 'categories' && <CategoriesTab />}
    </div>
  );
};

export default AdminProductsPage;
